import React, { useEffect, useState } from 'react';
import { getGastos, createGasto, getReporteGastos } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Receipt, TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

const COLORS = ['hsl(161, 91%, 16%)', 'hsl(160, 84%, 39%)', 'hsl(28, 87%, 37%)', 'hsl(0, 72%, 42%)', 'hsl(222, 47%, 11%)', 'hsl(240, 4%, 46%)'];

const categoriaLabels = {
    nomina: 'Nómina',
    operativo: 'Operativo',
    servicios: 'Servicios',
    mantenimiento: 'Mantenimiento',
    marketing: 'Marketing',
    otros: 'Otros',
};

const categoriaColors = {
    nomina: 'bg-primary/10 text-primary',
    operativo: 'bg-accent/10 text-accent',
    servicios: 'bg-warning/10 text-warning',
    mantenimiento: 'bg-orange-500/10 text-orange-500',
    marketing: 'bg-purple-500/10 text-purple-500',
    otros: 'bg-muted text-muted-foreground',
};

export default function ContabilidadPage() {
    const [gastos, setGastos] = useState([]);
    const [reporte, setReporte] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [mesActual, setMesActual] = useState(new Date().toISOString().slice(0, 7));
    const [form, setForm] = useState({
        categoria: '',
        concepto: '',
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        proveedor: '',
        factura: '',
        notas: '',
    });

    useEffect(() => {
        loadData();
    }, [mesActual]);

    const loadData = async () => {
        try {
            const [gastosRes, reporteRes] = await Promise.all([
                getGastos({ fecha_desde: `${mesActual}-01`, fecha_hasta: `${mesActual}-31` }),
                getReporteGastos(mesActual)
            ]);
            setGastos(gastosRes.data);
            setReporte(reporteRes.data);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...form,
                monto: parseFloat(form.monto),
            };
            await createGasto(data);
            toast.success('Gasto registrado');
            setDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar gasto');
        }
    };

    const resetForm = () => {
        setForm({
            categoria: '',
            concepto: '',
            monto: '',
            fecha: new Date().toISOString().split('T')[0],
            proveedor: '',
            factura: '',
            notas: '',
        });
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    const pieData = reporte?.por_categoria 
        ? Object.entries(reporte.por_categoria).map(([name, data]) => ({
            name: categoriaLabels[name] || name,
            value: data.total
        }))
        : [];

    return (
        <div className="space-y-6" data-testid="contabilidad-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Contabilidad</h1>
                    <p className="text-muted-foreground mt-1">Control de gastos e ingresos</p>
                </div>
                <div className="flex gap-2">
                    <Input
                        type="month"
                        value={mesActual}
                        onChange={(e) => setMesActual(e.target.value)}
                        className="w-40"
                    />
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={resetForm} data-testid="new-gasto-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nuevo Gasto
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-heading">Registrar Gasto</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Categoría</Label>
                                        <Select
                                            value={form.categoria}
                                            onValueChange={(value) => setForm({ ...form, categoria: value })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Seleccionar" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="operativo">Operativo</SelectItem>
                                                <SelectItem value="servicios">Servicios</SelectItem>
                                                <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                                                <SelectItem value="marketing">Marketing</SelectItem>
                                                <SelectItem value="otros">Otros</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Fecha</Label>
                                        <Input
                                            type="date"
                                            value={form.fecha}
                                            onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Concepto</Label>
                                    <Input
                                        value={form.concepto}
                                        onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                                        required
                                        placeholder="Descripción del gasto"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Monto</Label>
                                        <Input
                                            type="number"
                                            value={form.monto}
                                            onChange={(e) => setForm({ ...form, monto: e.target.value })}
                                            required
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>No. Factura</Label>
                                        <Input
                                            value={form.factura}
                                            onChange={(e) => setForm({ ...form, factura: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Proveedor</Label>
                                    <Input
                                        value={form.proveedor}
                                        onChange={(e) => setForm({ ...form, proveedor: e.target.value })}
                                    />
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" disabled={!form.categoria}>
                                        Registrar
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Resumen */}
            {reporte && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="animate-fadeIn">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Ingresos</p>
                                    <p className="text-2xl font-heading font-bold text-accent">
                                        {formatCurrency(reporte.total_ingresos)}
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-accent/10">
                                    <TrendingUp className="w-6 h-6 text-accent" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="animate-fadeIn stagger-1">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Gastos</p>
                                    <p className="text-2xl font-heading font-bold text-destructive">
                                        {formatCurrency(reporte.total_gastos)}
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-destructive/10">
                                    <TrendingDown className="w-6 h-6 text-destructive" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="animate-fadeIn stagger-2">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Balance</p>
                                    <p className={`text-2xl font-heading font-bold ${reporte.balance >= 0 ? 'text-primary' : 'text-destructive'}`}>
                                        {formatCurrency(reporte.balance)}
                                    </p>
                                </div>
                                <div className={`p-3 rounded-lg ${reporte.balance >= 0 ? 'bg-primary/10' : 'bg-destructive/10'}`}>
                                    <Wallet className={`w-6 h-6 ${reporte.balance >= 0 ? 'text-primary' : 'text-destructive'}`} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Gastos por Categoría</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            {pieData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={80}
                                            paddingAngle={4}
                                            dataKey="value"
                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-muted-foreground">
                                    Sin gastos este período
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Desglose por Categoría</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {Object.entries(reporte?.por_categoria || {}).map(([cat, data]) => (
                                <div key={cat} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Badge variant="outline" className={categoriaColors[cat]}>
                                            {categoriaLabels[cat]}
                                        </Badge>
                                        <span className="text-sm text-muted-foreground">{data.cantidad} registros</span>
                                    </div>
                                    <span className="font-mono font-bold">{formatCurrency(data.total)}</span>
                                </div>
                            ))}
                            {Object.keys(reporte?.por_categoria || {}).length === 0 && (
                                <p className="text-center text-muted-foreground py-8">Sin gastos registrados</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">Últimos Gastos</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>Concepto</TableHead>
                                    <TableHead>Categoría</TableHead>
                                    <TableHead className="text-right">Monto</TableHead>
                                    <TableHead className="hidden md:table-cell">Registrado por</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {gastos.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-12">
                                            <Receipt className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                            <p className="text-muted-foreground">No hay gastos registrados</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    gastos.slice(0, 20).map((gasto) => (
                                        <TableRow key={gasto.id} className="animate-fadeIn">
                                            <TableCell className="font-mono">{gasto.fecha}</TableCell>
                                            <TableCell>
                                                <p className="font-medium">{gasto.concepto}</p>
                                                {gasto.proveedor && (
                                                    <p className="text-xs text-muted-foreground">{gasto.proveedor}</p>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={categoriaColors[gasto.categoria]}>
                                                    {categoriaLabels[gasto.categoria]}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right font-mono font-bold text-destructive">
                                                -{formatCurrency(gasto.monto)}
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell text-muted-foreground">
                                                {gasto.registrado_por}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
